import React, { useState, useEffect } from 'react';
import { History, Printer, Eye, Check, AlertTriangle, Coins, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import apiService from '../../services/apiService';
import { formatMoney } from '../../utils/formatters';
import './CorteCajaPage.css';

// Denominaciones de billetes y monedas mexicanas
const DENOMINACIONES = {
    billetes: [
        { valor: 1000, label: '$1,000' },
        { valor: 500, label: '$500' },
        { valor: 200, label: '$200' },
        { valor: 100, label: '$100' },
        { valor: 50, label: '$50' },
        { valor: 20, label: '$20' }
    ],
    monedas: [
        { valor: 20, label: '$20' },
        { valor: 10, label: '$10' },
        { valor: 5, label: '$5' },
        { valor: 2, label: '$2' },
        { valor: 1, label: '$1' },
        { valor: 0.5, label: '$0.50' }
    ]
};

const CorteCajaPage = () => {
    const { loading, setLoading, showToast, user } = useApp();

    // Estado principal
    const [totales, setTotales] = useState({
        cantidad_ventas: 0,
        total_ventas: 0,
        total_efectivo: 0,
        total_efectivo_bruto: 0,
        total_tarjeta: 0,
        total_transferencia: 0,
        total_ingresos: 0,
        cantidad_ingresos: 0,
        total_retiros: 0,
        cantidad_retiros: 0
    });
    const [historialCortes, setHistorialCortes] = useState([]);

    // Inputs del usuario
    const [fondoInicial, setFondoInicial] = useState('');
    const [observaciones, setObservaciones] = useState('');

    // Estado para conteo de denominaciones
    const [conteoBilletes, setConteoBilletes] = useState({
        1000: '',
        500: '',
        200: '',
        100: '',
        50: '',
        20: ''
    });
    const [conteoMonedas, setConteoMonedas] = useState({
        20: '',
        10: '',
        5: '',
        2: '',
        1: '',
        0.5: ''
    });

    // Modales
    const [showDetalleModal, setShowDetalleModal] = useState(false);
    const [corteDetalle, setCorteDetalle] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Calcular total de billetes
    const totalBilletes = DENOMINACIONES.billetes.reduce((sum, d) => {
        const cantidad = parseInt(conteoBilletes[d.valor]) || 0;
        return sum + (cantidad * d.valor);
    }, 0);

    // Calcular total de monedas
    const totalMonedas = DENOMINACIONES.monedas.reduce((sum, d) => {
        const cantidad = parseInt(conteoMonedas[d.valor]) || 0;
        return sum + (cantidad * d.valor);
    }, 0);

    // Efectivo contado total
    const efectivoContado = totalBilletes + totalMonedas;

    // Cálculos
    const fondoInicialNum = parseFloat(fondoInicial) || 0;
    // Efectivo esperado = fondo + ventas efectivo + ingresos - retiros
    const efectivoVentas = totales.total_efectivo_bruto ?? totales.total_efectivo ?? 0;
    const efectivoEsperado = fondoInicialNum + efectivoVentas + totales.total_ingresos - totales.total_retiros;
    const diferencia = efectivoContado - efectivoEsperado;

    // Cargar datos al montar
    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        const nombrePc = localStorage.getItem('pos_nombre_pc');
        if (!nombrePc) {
            showToast('No se ha identificado la caja. Configure el identificador en Configuración.', 'error');
            return;
        }

        setLoading(true);
        try {
            // Cargar ventas pendientes (solo para totales)
            const resultVentas = await apiService.getVentasPendientesCorte(nombrePc);
            if (resultVentas.success) {
                const t = resultVentas.data.totales || {};
                setTotales({
                    cantidad_ventas: t.cantidad_ventas || 0,
                    total_ventas: t.total_ventas || 0,
                    total_efectivo: t.total_efectivo || 0,
                    total_efectivo_bruto: t.total_efectivo_bruto || 0,
                    total_tarjeta: t.total_tarjeta || 0,
                    total_transferencia: t.total_transferencia || 0,
                    total_ingresos: t.total_ingresos || 0,
                    cantidad_ingresos: t.cantidad_ingresos || 0,
                    total_retiros: t.total_retiros || 0,
                    cantidad_retiros: t.cantidad_retiros || 0
                });
            }

            // Cargar historial
            const resultHistorial = await apiService.getHistorialCortes(nombrePc);
            if (resultHistorial.success) {
                setHistorialCortes(resultHistorial.data.cortes || []);
            }
        } catch (error) {
            console.error('Error cargando datos:', error);
            showToast('Error al cargar datos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleConteoBilleteChange = (valor, cantidad) => {
        setConteoBilletes(prev => ({
            ...prev,
            [valor]: cantidad
        }));
    };

    const handleConteoMonedaChange = (valor, cantidad) => {
        setConteoMonedas(prev => ({
            ...prev,
            [valor]: cantidad
        }));
    };

    const limpiarConteo = () => {
        setConteoBilletes({ 1000: '', 500: '', 200: '', 100: '', 50: '', 20: '' });
        setConteoMonedas({ 20: '', 10: '', 5: '', 2: '', 1: '', 0.5: '' });
    };

    const handleHacerCorte = () => {
        // Permitir corte si hay ventas, ingresos o retiros
        const hayMovimientos = totales.cantidad_ventas > 0 || totales.cantidad_ingresos > 0 || totales.cantidad_retiros > 0;
        if (!hayMovimientos) {
            showToast('No hay movimientos para incluir en el corte', 'warning');
            return;
        }

        if (fondoInicialNum < 0) {
            showToast('El fondo inicial no puede ser negativo', 'warning');
            return;
        }

        if (efectivoContado <= 0) {
            showToast('Debe realizar el conteo de efectivo', 'warning');
            return;
        }

        setShowConfirmModal(true);
    };

    const confirmarCorte = async () => {
        const nombrePc = localStorage.getItem('pos_nombre_pc');
        const userId = user?.id || user?.usuario_id || user?.user_id;
        const userName = user?.usuario || user?.nombre || user?.username || user?.user;

        if (!userId) {
            showToast('No se pudo identificar el usuario', 'error');
            return;
        }

        setLoading(true);
        try {
            const result = await apiService.crearCorte({
                nombre_pc: nombrePc,
                usuario_id: userId,
                usuario_nombre: userName,
                fondo_inicial: fondoInicialNum,
                efectivo_contado: efectivoContado,
                observaciones: observaciones.trim()
            });

            if (result.success) {
                showToast('Corte de caja realizado exitosamente', 'success');
                setShowConfirmModal(false);

                // Limpiar formulario
                setFondoInicial('');
                setObservaciones('');
                limpiarConteo();

                // Recargar datos
                await cargarDatos();

                // Imprimir automáticamente el ticket
                await imprimirCorte(result.data.corte_id);
            } else {
                showToast(result.message || 'Error al crear corte', 'error');
            }
        } catch (error) {
            console.error('Error creando corte:', error);
            showToast('Error al crear corte', 'error');
        } finally {
            setLoading(false);
        }
    };

    const verDetalleCorte = async (corteId) => {
        setLoading(true);
        try {
            const result = await apiService.getDetalleCorte(corteId);
            if (result.success) {
                setCorteDetalle(result.data);
                setShowDetalleModal(true);
            } else {
                showToast('Error al obtener detalle del corte', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('Error al obtener detalle', 'error');
        } finally {
            setLoading(false);
        }
    };

    const imprimirCorte = async (corteId) => {
        try {
            const result = await apiService.imprimirCorte(corteId);
            if (result.success && result.data.html) {
                // Verificar si estamos en Electron
                if (window.electron && window.electron.ipcRenderer) {
                    try {
                        // Obtener la impresora predeterminada
                        const defaultPrinterResult = await window.electron.ipcRenderer.invoke('get-default-printer');

                        if (defaultPrinterResult.success && defaultPrinterResult.printer) {
                            const printerName = defaultPrinterResult.printer.name;
                            console.log('Imprimiendo corte en:', printerName);

                            // Imprimir directamente usando el handler de impresión térmica
                            const printResult = await window.electron.ipcRenderer.invoke('print-thermal-receipt', {
                                printerName: printerName,
                                textContent: result.data.html,
                                silent: true
                            });

                            if (printResult.success) {
                                showToast('Ticket de corte impreso correctamente', 'success');
                            } else {
                                console.error('Error en impresión:', printResult.error);
                                showToast(`Error al imprimir: ${printResult.error || 'Error desconocido'}`, 'error');
                            }
                        } else {
                            showToast('No se encontró una impresora predeterminada', 'error');
                        }
                    } catch (electronError) {
                        console.error('Error con Electron print:', electronError);
                        // Fallback a impresión web
                        imprimirCorteWeb(result.data.html);
                    }
                } else {
                    // Fallback para navegador web (no Electron)
                    imprimirCorteWeb(result.data.html);
                }
            } else {
                showToast('Error al generar ticket', 'error');
            }
        } catch (error) {
            console.error('Error imprimiendo:', error);
            showToast('Error al imprimir', 'error');
        }
    };

    // Función de fallback para imprimir en navegador web
    const imprimirCorteWeb = (html) => {
        const ventanaImpresion = window.open('', 'Corte', 'width=400,height=600');
        if (ventanaImpresion) {
            ventanaImpresion.document.write(html);
            ventanaImpresion.document.close();
            ventanaImpresion.print();
        }
    };

    const formatFecha = (fecha) => {
        if (!fecha) return '';
        const date = new Date(fecha);
        return date.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getMetodoPagoLabel = (metodo) => {
        const labels = {
            'efectivo': 'Efectivo',
            'tarjeta': 'Tarjeta',
            'transferencia': 'Transferencia',
            'mixto': 'Mixto'
        };
        return labels[metodo] || metodo;
    };

    return (
        <div className="corte-caja-page">
            <div className="corte-header">
                <h1>Corte de Caja</h1>
                <p className="corte-subtitle">Arqueo y cierre de caja</p>
            </div>


            {/* Contenedor principal */}
            <div className="corte-content">
                {/* Sección de Arqueo */}
                <div className="arqueo-section">
                    <h2>
                        <Coins size={20} />
                        Arqueo de Caja
                    </h2>

                    <div className="arqueo-grid">
                        {/* Billetes */}
                        <div className="denominaciones-box">
                            <h3>Billetes</h3>
                            <div className="denominaciones-list">
                                {DENOMINACIONES.billetes.map(d => {
                                    const cantidad = parseInt(conteoBilletes[d.valor]) || 0;
                                    const subtotal = cantidad * d.valor;
                                    return (
                                        <div key={`billete-${d.valor}`} className="denominacion-row">
                                            <span className="denominacion-label">{d.label}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="0"
                                                value={conteoBilletes[d.valor]}
                                                onChange={(e) => handleConteoBilleteChange(d.valor, e.target.value)}
                                                className="denominacion-input"
                                            />
                                            <span className="denominacion-subtotal">
                                                {formatMoney(subtotal)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="denominaciones-total">
                                <span>Total Billetes:</span>
                                <span>{formatMoney(totalBilletes)}</span>
                            </div>
                        </div>

                        {/* Monedas */}
                        <div className="denominaciones-box">
                            <h3>Monedas</h3>
                            <div className="denominaciones-list">
                                {DENOMINACIONES.monedas.map(d => {
                                    const cantidad = parseInt(conteoMonedas[d.valor]) || 0;
                                    const subtotal = cantidad * d.valor;
                                    return (
                                        <div key={`moneda-${d.valor}`} className="denominacion-row">
                                            <span className="denominacion-label">{d.label}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="0"
                                                value={conteoMonedas[d.valor]}
                                                onChange={(e) => handleConteoMonedaChange(d.valor, e.target.value)}
                                                className="denominacion-input"
                                            />
                                            <span className="denominacion-subtotal">
                                                {formatMoney(subtotal)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="denominaciones-total">
                                <span>Total Monedas:</span>
                                <span>{formatMoney(totalMonedas)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="arqueo-total-box">
                        <span>TOTAL EFECTIVO CONTADO:</span>
                        <span className="arqueo-total-value">{formatMoney(efectivoContado)}</span>
                    </div>

                    <button className="btn-limpiar" onClick={limpiarConteo}>
                        Limpiar Conteo
                    </button>
                </div>

                {/* Formulario de corte */}
                <div className="corte-form-section">
                    <h2>Realizar Corte</h2>

                    <div className="corte-form">
                        <div className="form-group">
                            <label>Fondo Inicial (efectivo antes de ventas)</label>
                            <Input
                                type="number"
                                placeholder="0.00"
                                value={fondoInicial}
                                onChange={(e) => setFondoInicial(e.target.value)}
                                min="0"
                                step="0.01"
                            />
                        </div>

                        <div className="calculo-box">
                            <div className="calculo-row">
                                <span>Fondo Inicial:</span>
                                <span>{formatMoney(fondoInicialNum)}</span>
                            </div>
                            <div className="calculo-row">
                                <span>+ Ventas Efectivo:</span>
                                <span>{formatMoney(efectivoVentas)}</span>
                            </div>
                            {totales.total_ingresos > 0 && (
                                <div className="calculo-row ingreso">
                                    <span>
                                        <ArrowDownCircle size={14} style={{ marginRight: 4, color: '#059669' }} />
                                        + Ingresos ({totales.cantidad_ingresos}):
                                    </span>
                                    <span style={{ color: '#059669' }}>+{formatMoney(totales.total_ingresos)}</span>
                                </div>
                            )}
                            {totales.total_retiros > 0 && (
                                <div className="calculo-row retiro">
                                    <span>
                                        <ArrowUpCircle size={14} style={{ marginRight: 4, color: '#dc2626' }} />
                                        - Retiros ({totales.cantidad_retiros}):
                                    </span>
                                    <span style={{ color: '#dc2626' }}>-{formatMoney(totales.total_retiros)}</span>
                                </div>
                            )}
                            <div className="calculo-row esperado">
                                <span>= Esperado en Caja:</span>
                                <span>{formatMoney(efectivoEsperado)}</span>
                            </div>
                            <div className="calculo-row contado">
                                <span>Efectivo Contado:</span>
                                <span>{formatMoney(efectivoContado)}</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Observaciones (opcional)</label>
                            <textarea
                                placeholder="Notas o comentarios sobre el corte..."
                                value={observaciones}
                                onChange={(e) => setObservaciones(e.target.value)}
                                rows={3}
                            />
                        </div>

                        <Button
                            onClick={handleHacerCorte}
                            disabled={loading || (totales.cantidad_ventas === 0 && totales.cantidad_ingresos === 0 && totales.cantidad_retiros === 0)}
                            className="btn-hacer-corte"
                        >
                            <Check size={20} />
                            Hacer Corte
                        </Button>
                    </div>
                </div>
            </div>

            {/* Historial de cortes */}
            <div className="historial-section">
                <h2>
                    <History size={20} />
                    Historial de Cortes
                </h2>

                {historialCortes.length === 0 ? (
                    <p className="no-historial">No hay cortes anteriores</p>
                ) : (
                    <div className="historial-table-container">
                        <table className="historial-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Fecha</th>
                                    <th>Cajero</th>
                                    <th>Ventas</th>
                                    <th>Total</th>
                                    <th>Diferencia</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historialCortes.map(corte => (
                                    <tr key={corte.id}>
                                        <td>#{corte.id}</td>
                                        <td>{formatFecha(corte.fecha_creacion)}</td>
                                        <td>{corte.usuario_nombre}</td>
                                        <td>{corte.cantidad_ventas}</td>
                                        <td>{formatMoney(corte.total_ventas)}</td>
                                        <td className={corte.diferencia >= 0 ? 'diferencia-ok' : 'diferencia-mal'}>
                                            {corte.diferencia >= 0 ? '+' : ''}{formatMoney(corte.diferencia)}
                                        </td>
                                        <td className="acciones">
                                            <button
                                                className="btn-icon"
                                                onClick={() => verDetalleCorte(corte.id)}
                                                title="Ver detalle"
                                            >
                                                <Eye size={18} />
                                            </button>
                                            <button
                                                className="btn-icon"
                                                onClick={() => imprimirCorte(corte.id)}
                                                title="Imprimir"
                                            >
                                                <Printer size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de confirmación */}
            {showConfirmModal && (
                <Modal
                    title="Confirmar Corte de Caja"
                    onClose={() => setShowConfirmModal(false)}
                    size="medium"
                >
                    <div className="confirm-modal-content">
                        <div className="confirm-summary">
                            <div className="summary-row">
                                <span>Ventas a incluir:</span>
                                <span>{totales.cantidad_ventas}</span>
                            </div>
                            <div className="summary-row">
                                <span>Total en ventas:</span>
                                <span>{formatMoney(totales.total_ventas)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Fondo inicial:</span>
                                <span>{formatMoney(fondoInicialNum)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Ventas en efectivo:</span>
                                <span>{formatMoney(efectivoVentas)}</span>
                            </div>
                            {totales.total_ingresos > 0 && (
                                <div className="summary-row" style={{ color: '#059669' }}>
                                    <span>
                                        <ArrowDownCircle size={14} style={{ marginRight: 4 }} />
                                        Ingresos ({totales.cantidad_ingresos}):
                                    </span>
                                    <span>+{formatMoney(totales.total_ingresos)}</span>
                                </div>
                            )}
                            {totales.total_retiros > 0 && (
                                <div className="summary-row" style={{ color: '#dc2626' }}>
                                    <span>
                                        <ArrowUpCircle size={14} style={{ marginRight: 4 }} />
                                        Retiros ({totales.cantidad_retiros}):
                                    </span>
                                    <span>-{formatMoney(totales.total_retiros)}</span>
                                </div>
                            )}
                            <div className="summary-row">
                                <span>Efectivo esperado:</span>
                                <span>{formatMoney(efectivoEsperado)}</span>
                            </div>
                            <div className="summary-row">
                                <span>Efectivo contado:</span>
                                <span>{formatMoney(efectivoContado)}</span>
                            </div>
                            <div className={`summary-row diferencia ${diferencia >= 0 ? 'positiva' : 'negativa'}`}>
                                <span>Diferencia:</span>
                                <span>{diferencia >= 0 ? '+' : ''}{formatMoney(diferencia)}</span>
                            </div>
                        </div>

                        {diferencia !== 0 && (
                            <div className={`alert-box ${diferencia < 0 ? 'alert-danger' : 'alert-warning'}`}>
                                <AlertTriangle size={20} />
                                <span>
                                    {diferencia < 0
                                        ? `Faltante de ${formatMoney(Math.abs(diferencia))} en caja`
                                        : `Sobrante de ${formatMoney(diferencia)} en caja`
                                    }
                                </span>
                            </div>
                        )}

                        <div className="confirm-actions">
                            <Button
                                variant="outline"
                                onClick={() => setShowConfirmModal(false)}
                                disabled={loading}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={confirmarCorte}
                                disabled={loading}
                            >
                                {loading ? 'Procesando...' : 'Confirmar Corte'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de detalle */}
            {showDetalleModal && corteDetalle && (
                <Modal
                    title={`Detalle del Corte #${corteDetalle.corte.id}`}
                    onClose={() => {
                        setShowDetalleModal(false);
                        setCorteDetalle(null);
                    }}
                    size="large"
                >
                    <div className="detalle-modal-content">
                        <div className="detalle-info">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Fecha:</span>
                                    <span className="info-value">{formatFecha(corteDetalle.corte.fecha_creacion)}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Cajero:</span>
                                    <span className="info-value">{corteDetalle.corte.usuario_nombre}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Caja:</span>
                                    <span className="info-value">{corteDetalle.corte.identificador || 'N/A'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Ventas:</span>
                                    <span className="info-value">{corteDetalle.corte.cantidad_ventas}</span>
                                </div>
                            </div>

                            <div className="totales-detalle">
                                <div className="total-item">
                                    <span>Efectivo:</span>
                                    <span>{formatMoney(corteDetalle.corte.total_efectivo)}</span>
                                </div>
                                <div className="total-item">
                                    <span>Tarjeta:</span>
                                    <span>{formatMoney(corteDetalle.corte.total_tarjeta)}</span>
                                </div>
                                <div className="total-item">
                                    <span>Transferencia:</span>
                                    <span>{formatMoney(corteDetalle.corte.total_transferencia)}</span>
                                </div>
                                <div className="total-item total">
                                    <span>Total Ventas:</span>
                                    <span>{formatMoney(corteDetalle.corte.total_ventas)}</span>
                                </div>
                            </div>

                            <div className="arqueo-detalle">
                                <div className="arqueo-item">
                                    <span>Fondo Inicial:</span>
                                    <span>{formatMoney(corteDetalle.corte.fondo_inicial)}</span>
                                </div>
                                <div className="arqueo-item">
                                    <span>+ Efectivo Ventas:</span>
                                    <span>{formatMoney(corteDetalle.corte.total_efectivo)}</span>
                                </div>
                                {corteDetalle.corte.total_ingresos > 0 && (
                                    <div className="arqueo-item" style={{ color: '#059669' }}>
                                        <span>
                                            <ArrowDownCircle size={14} style={{ marginRight: 4 }} />
                                            + Ingresos:
                                        </span>
                                        <span>+{formatMoney(corteDetalle.corte.total_ingresos)}</span>
                                    </div>
                                )}
                                {corteDetalle.corte.total_retiros > 0 && (
                                    <div className="arqueo-item" style={{ color: '#dc2626' }}>
                                        <span>
                                            <ArrowUpCircle size={14} style={{ marginRight: 4 }} />
                                            - Retiros:
                                        </span>
                                        <span>-{formatMoney(corteDetalle.corte.total_retiros)}</span>
                                    </div>
                                )}
                                <div className="arqueo-item">
                                    <span>= Esperado:</span>
                                    <span>{formatMoney(corteDetalle.corte.efectivo_esperado)}</span>
                                </div>
                                <div className="arqueo-item">
                                    <span>Contado:</span>
                                    <span>{formatMoney(corteDetalle.corte.efectivo_contado)}</span>
                                </div>
                                <div className={`arqueo-item diferencia ${corteDetalle.corte.diferencia >= 0 ? 'positiva' : 'negativa'}`}>
                                    <span>Diferencia:</span>
                                    <span>
                                        {corteDetalle.corte.diferencia >= 0 ? '+' : ''}
                                        {formatMoney(corteDetalle.corte.diferencia)}
                                    </span>
                                </div>
                            </div>

                            {corteDetalle.corte.observaciones && (
                                <div className="observaciones-detalle">
                                    <strong>Observaciones:</strong>
                                    <p>{corteDetalle.corte.observaciones}</p>
                                </div>
                            )}
                        </div>

                        <div className="ventas-detalle">
                            <h3>Ventas incluidas ({corteDetalle.ventas.length})</h3>
                            <table className="ventas-table-mini">
                                <thead>
                                    <tr>
                                        <th>Folio</th>
                                        <th>Hora</th>
                                        <th>Método</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {corteDetalle.ventas.map(venta => (
                                        <tr key={venta.id}>
                                            <td>{venta.folio}</td>
                                            <td>{formatFecha(venta.fecha_creacion)}</td>
                                            <td>{getMetodoPagoLabel(venta.metodo_pago)}</td>
                                            <td>{formatMoney(venta.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="detalle-actions">
                            <Button
                                variant="outline"
                                onClick={() => imprimirCorte(corteDetalle.corte.id)}
                            >

                                Imprimir
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowDetalleModal(false);
                                    setCorteDetalle(null);
                                }}
                            >
                                Cerrar
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default CorteCajaPage;
